import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import aboutImage from '@/assets/iphones.webp';

import checkIcon from '@/assets/EventSection/check-icon.svg';
import { useInView } from '@/hooks/useInView';
import { color } from '@/themes/color';
import { typography } from '@/themes/typography';

const FEATURE_STAGGER_MS = 120;

const FEATURES = [
  'Interactive maps of vineyards across every UK region',
  'Tour, tasting and cellar door information',
  'Dining, accommodation and travel links',
  'Event listings and seasonal calendars',
  'Editorial features and expert recommendations',
];

export default function AboutSection(): JSX.Element {
  const [sectionRef, inView] = useInView({
    threshold: 0.35,
    rootMargin: '0px 0px -80px 0px',
    unobserveOnce: true,
  });

  useEffect(() => {
    if (inView) {
      gtag('event', 'section_view', {
        event_category: 'Engagement',
        event_label: 'About Section',
      });
    }
  }, [inView]);

  return (
    <Box
      ref={sectionRef}
      component="section"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: { xs: 'auto', md: '70vh' },
        backgroundColor: color.merlot['400'],
        pt: { xs: '48px', md: '64px' },
        px: { xs: '24px', md: '64px' },
        gap: { xs: '48px', md: '104px' },
        overflowX: 'hidden',
        overflowY: 'visible',
      }}
    >
      <Typography
        component="h2"
        sx={{
          ...typography.display,
          color: color.cream['100'],
          textAlign: 'center',
          maxWidth: '840px',
          mx: 'auto',
          lineHeight: 1.1,
          fontSize: {
            xs: 'var(--font-size-36, 36px)',
            sm: 'var(--font-size-48, 48px)',
            md: typography.display.fontSize,
          },
        }}
      >
        The home of English and
        <br />
        Welsh wine tourism
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'stretch' },
          gap: { xs: '28px', md: '44px' },
          flex: 1,
        }}
      >
        <Box
          sx={{
            flex: { md: '1 1 58%' },
            minWidth: { md: 0 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            px: { xs: '0px', md: '32px' },
          }}
        >
          <Typography
            sx={{
              ...typography.heading4,
              fontWeight: 350,
              color: color.cream['100'],
              mb: '32px',
              maxWidth: '600px',
              lineHeight: 1.35,
              fontSize: {
                xs: 'var(--font-size-18, 18px)',
                sm: 'var(--font-size-20, 20px)',
                md: typography.heading4.fontSize,
              },
            }}
          >
            Our app brings together everything you need to experience English
            and Welsh vineyards with ease
          </Typography>

          <Box
            component="ul"
            sx={{
              listStyle: 'none',
              p: 0,
              m: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              '@keyframes aboutFeatureIn': {
                from: { opacity: 0, transform: 'translateX(-14px)' },
                to: { opacity: 1, transform: 'translateX(0)' },
              },
            }}
          >
            {FEATURES.map((feature, index) => (
              <Box
                key={feature}
                component="li"
                sx={{
                  ...typography.heading4,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  color: color.cream['100'],
                  lineHeight: 1.35,
                  fontWeight: 350,
                  fontSize: {
                    xs: 'var(--font-size-18, 18px)',
                    sm: 'var(--font-size-20, 20px)',
                    md: typography.heading4.fontSize,
                  },
                  opacity: 0,
                  ...(inView && {
                    animation: 'aboutFeatureIn 0.45s ease-out forwards',
                    animationDelay: `${index * FEATURE_STAGGER_MS}ms`,
                  }),
                }}
              >
                <Box
                  component="img"
                  src={checkIcon}
                  alt=""
                  aria-hidden
                  sx={{
                    width: 22,
                    height: 22,
                    flexShrink: 0,
                    mt: '2px',
                    display: 'block',
                  }}
                />
                {feature}
              </Box>
            ))}
          </Box>

          <Typography
            sx={{
              ...typography.heading4,
              fontWeight: 350,
              color: color.cream['100'],
              mt: '32px',
              maxWidth: '600px',
              lineHeight: 1.35,
              fontSize: {
                xs: 'var(--font-size-18, 18px)',
                sm: 'var(--font-size-20, 20px)',
                md: typography.heading4.fontSize,
              },
            }}
          >
            All curated in one easy-to-use app designed to make wine tourism in
            the UK accessible, enjoyable and inspiring.
          </Typography>
        </Box>

        <Box
          sx={{
            flex: { md: '1 1 50%' },
            width: '100%',
            display: 'flex',
            justifyContent: { xs: 'center', md: 'flex-start' },
            alignItems: 'flex-end',
          }}
        >
          <Box
            component="img"
            src={aboutImage}
            alt="UK Vineyard Guide app displayed on iPhone screens"
            width={1248}
            height={1298}
            loading="lazy"
            sx={{
              '@keyframes aboutImageIn': {
                from: { opacity: 0, transform: 'translateX(44px)' },
                to: { opacity: 1, transform: 'translateX(0)' },
              },
              display: 'block',
              width: { xs: '100%', md: 'min(50vw, 560px)' },
              maxWidth: { xs: '100%', md: 'none' },
              height: 'auto',
              aspectRatio: '1248 / 1298',
              objectFit: 'contain',
              opacity: 0,
              transform: 'translateX(44px)',
              willChange: 'transform, opacity',
              ...(inView && {
                animation: 'aboutImageIn 0.75s ease-out forwards',
                animationDelay: `${FEATURE_STAGGER_MS * 2}ms`,
              }),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
